import type { PasskeyDto, PasskeyOptionsDto, SessionDto } from '@dijitalapp/shared';
import { api, ApiError } from './api';

// Loaded on demand: only users who touch a passkey control pay for the library.
const webauthn = () => import('@simplewebauthn/browser');

export class PasskeyCancelledError extends Error {}

export async function passkeysSupported(): Promise<boolean> {
  return (await webauthn()).browserSupportsWebAuthn();
}

/** The user closed the system dialog, timed out or picked nothing: not an error worth alarming about. */
function rethrow(err: unknown): never {
  if (err instanceof ApiError) throw err;
  if (err instanceof Error && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
    throw new PasskeyCancelledError(err.message);
  }
  throw new ApiError('PASSKEY_FAILED', 0);
}

export async function registerPasskey(name?: string): Promise<PasskeyDto> {
  const { startRegistration } = await webauthn();
  try {
    const { challengeId, options } = await api<PasskeyOptionsDto<Parameters<typeof startRegistration>[0]['optionsJSON']>>(
      'POST',
      '/passkeys/registration/options',
    );
    const response = await startRegistration({ optionsJSON: options });
    return await api<PasskeyDto>('POST', '/passkeys/registration/verify', { challengeId, response, ...(name ? { name } : {}) });
  } catch (err) {
    rethrow(err);
  }
}

export async function signInWithPasskey(): Promise<SessionDto> {
  const { startAuthentication } = await webauthn();
  try {
    const { challengeId, options } = await api<PasskeyOptionsDto<Parameters<typeof startAuthentication>[0]['optionsJSON']>>(
      'POST',
      '/auth/passkey/options',
    );
    const response = await startAuthentication({ optionsJSON: options });
    return await api<SessionDto>('POST', '/auth/passkey/verify', { challengeId, response });
  } catch (err) {
    rethrow(err);
  }
}

/** Device name suggestion for a new passkey, e.g. "Chrome · Windows". */
export function suggestPasskeyName(): string {
  const ua = navigator.userAgent;
  const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac/.test(ua) ? 'macOS' : 'Linux';
  const browser = /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : 'Safari';
  return `${browser} · ${os}`;
}
